import { type ReactNode } from 'react'
import { Head, useForm, usePage } from '@inertiajs/react'
import AdminLayout from '@/components/AdminLayout'
import ResearchNav from '@/components/admin/research/ResearchNav'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Upload } from 'lucide-react'

type ImportResult = {
  imported: number
  skipped: number
  failed: number
  warnings: string[]
  imported_rows: string[]
  skipped_rows: string[]
  failed_rows: { row: number | null; error: string }[]
  totals: Record<string, number>
}

type FlashWithImport = {
  flash?: {
    import_result?: ImportResult
  }
}

export default function ResearchImportNew() {
  const { flash } = usePage<FlashWithImport>().props
  const result = flash?.import_result ?? null
  const { data, setData, post, processing, errors } = useForm({
    research_import: {
      file: null as File | null,
      download_attachments: false,
    },
  })

  const form = data.research_import

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post('/admin/research/import', { forceFormData: true })
  }

  return (
    <>
      <Head title="Research - Import" />
      <div>
        <h1 className="text-3xl font-semibold tracking-tight font-serif">Research Database</h1>
        <div className="mt-4">
          <ResearchNav />
        </div>

        <div className="grid max-w-5xl grid-cols-1 gap-6 lg:grid-cols-3">
          <Card className="self-start lg:col-span-2">
            <CardHeader>
              <CardTitle>Import CSV</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="research-import-file">CSV file</Label>
                  <Input
                    id="research-import-file"
                    type="file"
                    accept=".csv,text/csv"
                    onChange={e =>
                      setData('research_import', {
                        ...form,
                        file: e.target.files?.[0] ?? null,
                      })
                    }
                  />
                  {errors['research_import.file'] && (
                    <p className="text-xs text-destructive">{errors['research_import.file']}</p>
                  )}
                </div>

                <div className="flex items-start gap-3 rounded-lg border border-black/10 p-3">
                  <Checkbox
                    id="research-import-attachments"
                    checked={form.download_attachments}
                    onCheckedChange={checked =>
                      setData('research_import', {
                        ...form,
                        download_attachments: checked === true,
                      })
                    }
                  />
                  <div className="space-y-1">
                    <Label htmlFor="research-import-attachments">Download linked images and files</Label>
                    <p className="text-xs text-muted-foreground">
                      When off, image and flyer links are saved as custom field URLs.
                    </p>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button type="submit" disabled={processing || !form.file}>
                    <Upload className="mr-2 size-4" />
                    Import
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          <Card className="self-start">
            <CardHeader>
              <CardTitle>Current Totals</CardTitle>
            </CardHeader>
            <CardContent>
              {result ? (
                <dl className="space-y-2 text-sm">
                  {Object.entries(result.totals).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between gap-3">
                      <dt className="capitalize text-muted-foreground">{key.split('_').join(' ')}</dt>
                      <dd className="font-medium">{value.toLocaleString()}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p className="text-sm text-muted-foreground">No import run in this session.</p>
              )}
            </CardContent>
          </Card>
        </div>

        {result && (
          <div className="mt-6 grid max-w-5xl grid-cols-1 gap-6 lg:grid-cols-3">
            <Card>
              <CardHeader>
                <CardTitle>Summary</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                <Badge variant="secondary">{result.imported} imported</Badge>
                <Badge variant="outline">{result.skipped} skipped</Badge>
                <Badge variant={result.failed > 0 ? 'destructive' : 'secondary'}>
                  {result.failed} failed
                </Badge>
                {result.warnings.length > 0 && (
                  <Badge variant="outline">{result.warnings.length} warnings</Badge>
                )}
              </CardContent>
            </Card>

            <ResultList title="Imported" items={result.imported_rows} />
            <ResultList title="Skipped" items={result.skipped_rows} />

            {(result.failed_rows.length > 0 || result.warnings.length > 0) && (
              <Card className="lg:col-span-3">
                <CardHeader>
                  <CardTitle>Needs Review</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  {result.failed_rows.map(row => (
                    <p key={`${row.row}-${row.error}`} className="text-destructive">
                      {row.row === null ? 'File' : `Row ${row.row}`}: {row.error}
                    </p>
                  ))}
                  {result.warnings.map(warning => (
                    <p key={warning} className="text-muted-foreground">{warning}</p>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </>
  )
}

function ResultList({ title, items }: { title: string; items: string[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground">None</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {items.map(item => (
              <li key={item} className="text-muted-foreground">{item}</li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}

ResearchImportNew.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
